/**
 * Standard Actions configuration for Dawn.
 *
 * Storefront Renderer injects the Shopify Standard Actions bundle
 * (`window.Shopify.actions.{updateCart,openCart,getCart,…}`). This file
 * overrides the bundle's built-in Dawn refresh path with an explicit,
 * in-theme version so forks that change Dawn's cart contract keep
 * working. Remove this file and the built-in defaults take over.
 *
 *   - openCart   — opens <cart-drawer>; falls back to /cart.
 *   - updateCart — after the Storefront API mutation, refreshes the
 *     affected cart sections and publishes `cart-update` so Dawn's
 *     pubsub subscribers react.
 *   - other actions (getCart, etc.) keep the default implementation.
 */

// Cart custom elements that advertise sections via getSectionsToRender().
// If Dawn adds a new cart custom element, add its tag here.
const DAWN_CART_TAGS = ['cart-drawer', 'cart-items', 'cart-drawer-items', 'cart-notification'];

// Sections that Dawn's own pubsub subscribers refresh (cart.js's
// CartItems#onCartUpdate fetches and replaces these directly when
// cart-update fires; cart-drawer.js's renderContents handles the
// drawer body). We skip them here to avoid double-rendering.
// Format is '<element-tag>:<getSectionsToRender entry id>'.
// If you change which sections those subscribers refresh, update this set.
const DAWN_PUBSUB_REFRESHED_SECTIONS = new Set([
  'cart-drawer:cart-drawer',
  'cart-drawer-items:CartDrawer',
  'cart-items:main-cart-items',
]);

// Walk every mounted Dawn cart custom element, collect the sections it
// wants rendered, and dedupe. Returns a Map keyed by section id, each
// entry pointing at the DOM mount and the selector used to extract the
// fresh fragment.
function collectCartSections() {
  const sections = new Map();

  for (const el of document.querySelectorAll(DAWN_CART_TAGS.join(','))) {
    let entries;
    try {
      entries = el.getSectionsToRender?.();
    } catch {
      continue;
    }

    const tag = el.tagName.toLowerCase();
    for (const entry of entries ?? []) {
      if (DAWN_PUBSUB_REFRESHED_SECTIONS.has(`${tag}:${entry.id}`)) continue;

      const sectionId = entry.section ?? entry.id;
      if (!sectionId || sections.has(sectionId)) continue;

      // Two patterns coexist in getSectionsToRender():
      //   - cart-items style: entry.section is the parent Liquid section
      //     id, entry.selector is a child node inside it.
      //   - cart-drawer / cart-notification style: entry.id IS the mount;
      //     there is no parent wrapper.
      const root = entry.section ? document.getElementById(entry.id) : document;
      if (!root) continue;

      const mount = entry.selector
        ? (root.querySelector(entry.selector) ?? (entry.section ? root : null))
        : document.getElementById(entry.id);
      if (!mount) continue;

      sections.set(sectionId, {
        mount,
        extractSelector: entry.selector || '.shopify-section',
      });
    }
  }

  return sections;
}

// After a Storefront API mutation, refresh every Dawn cart section
// that isn't already refreshed by Dawn's own pubsub subscribers, then
// publish 'cart-update' so the subscribers run.
//
// We always fetch /cart.js (with sections= when we have any) so that
// `cartData` is defined for subscribers. quick-add-bulk.js reads
// `event.cartData.items` unconditionally — publishing without cartData
// makes it throw.
async function refreshDawnCartUI() {
  const sections = collectCartSections();
  const sectionsQuery = sections.size
    ? `?sections=${[...sections.keys()].join(',')}`
    : '';
  // `routes` is a Dawn global, but don't assume it's defined.
  const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
  const url = `${cartUrl}.js${sectionsQuery}`;
  const cartData = await fetch(url, { headers: { Accept: 'application/json' } })
    .then((r) => (r.ok ? r.json() : null))
    .catch(() => null);

  if (cartData?.sections) {
    for (const [id, { mount, extractSelector }] of sections) {
      const html = cartData.sections[id];
      if (!html) continue;
      const source = new DOMParser()
        .parseFromString(html, 'text/html')
        .querySelector(extractSelector);
      if (source) mount.replaceChildren(...source.childNodes);
    }
  }

  // Hand off to Dawn's existing subscribers. cartData is the full
  // Cart Ajax payload (items, item_count, token, …) plus sections;
  // quick-add-bulk.js and price-per-item.js read it directly.
  publish(PUB_SUB_EVENTS.cartUpdate, {
    source: 'external-refresh',
    cartData: cartData ?? undefined,
  });
}

// Shopping agents use Standard Actions rather than Timesact's product form.
// Copy the same configured properties before Shopify's single cart mutation.
const EEVA_PREORDER_ERROR = 'Could not verify the pre-order dispatch details. Please open the product page and use its purchase button. Nothing was added by this request.';
const EEVA_CART_DETAILS_QUERY = `query EevaCartLineDetails($id: ID!, $after: String) {
  cart(id: $id) {
    lines(first: 100, after: $after) {
      nodes { id quantity attributes { key value } merchandise { ... on ProductVariant { id } } }
      pageInfo { hasNextPage endCursor }
    }
  }
}`;

function eevaPreorderError() {
  return new Error(EEVA_PREORDER_ERROR);
}

async function eevaWithTimeout(promise, milliseconds = 8000) {
  let timer;
  try {
    return await Promise.race([
      promise,
      new Promise((_, reject) => { timer = setTimeout(() => reject(eevaPreorderError()), milliseconds); }),
    ]);
  } finally {
    clearTimeout(timer);
  }
}

async function eevaTimesactReady() {
  const deadline = Date.now() + 5000;
  do {
    const app = window.TimesactApp;
    if (typeof app?.storefrontClient?.fetchVariant === 'function' &&
        typeof app?.dataManager?.getActiveFeatures === 'function' &&
        typeof app?.dataManager?.getExperience === 'function') return app;
    if (window.TimesactData?.appDisabled || window.TimesactData?.useCustomScript) break;
    await new Promise((resolve) => setTimeout(resolve, 100));
  } while (Date.now() < deadline);
  throw eevaPreorderError();
}

function eevaVariantId(id) {
  const match = String(id || '').match(/^(?:gid:\/\/shopify\/ProductVariant\/)?(\d+)$/);
  if (!match) throw eevaPreorderError();
  return Number(match[1]);
}

async function eevaPreorderProperties(app, id) {
  const variantId = eevaVariantId(id);
  const variant = await eevaWithTimeout(app.storefrontClient.fetchVariant(variantId));
  if (!variant || variant.id !== variantId) throw eevaPreorderError();

  // An active state whose experience is missing must not become an ordinary add.
  for (const [experienceId, state] of Object.entries(variant.experiencesState || {})) {
    if (state?.preOrderStatus === 'ACTIVE' && !app.dataManager.getExperience(experienceId)) {
      throw eevaPreorderError();
    }
  }
  const features = app.dataManager.getActiveFeatures(variantId).filter((feature) => feature.type === 'preOrder');
  if (!features.length) return null;
  const properties = features.map(({ config }) => {
    const cart = config?.uiConfig?.cart;
    // Do not invent a delivery estimate or silently opt into a purchase option.
    if (config?.purchaseOptionMode !== 'WITHOUT_PURCHASE_OPTION' ||
        !cart?.labelKey?.trim() || !cart?.labelValue?.trim() ||
        /\{\{/.test(cart.labelValue)) throw eevaPreorderError();
    return { key: cart.labelKey, value: cart.labelValue };
  });
  const first = properties[0];
  if (properties.some((property) => property.key !== first.key || property.value !== first.value)) {
    throw eevaPreorderError();
  }
  return [first, { key: '_timesact_preorder', value: 'true' }];
}

async function eevaReadCartLines(payload, options) {
  const cartId = payload.cartId || (await window.Shopify.actions.getCart()).cart?.id;
  if (!cartId) throw eevaPreorderError();
  const lines = [];
  let after = null;
  do {
    const response = await fetch('/api/2026-07/graphql.json', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ query: EEVA_CART_DETAILS_QUERY, variables: { id: cartId, after } }),
      signal: options?.signal,
    });
    if (!response.ok) throw eevaPreorderError();
    const result = await response.json();
    const page = result.data?.cart?.lines;
    if (result.errors?.length || !page?.nodes || !page.pageInfo) throw eevaPreorderError();
    lines.push(...page.nodes);
    const next = page.pageInfo.hasNextPage ? page.pageInfo.endCursor : null;
    if (page.pageInfo.hasNextPage && (!next || next === after)) throw eevaPreorderError();
    after = next;
  } while (after);
  return lines;
}

async function eevaPrepareAgentCart(payload, options) {
  const requested = payload.lines || [];
  const positive = requested.filter((line) => !line.id || line.quantity > 0);
  if (!positive.length) return; // Deletion and non-line edits never need Timesact.
  const app = await eevaTimesactReady();
  let existing = [];
  if (positive.some((line) => line.id)) {
    existing = await eevaWithTimeout(eevaReadCartLines(payload, options));
  }
  // Standard Actions also accepts Ajax view keys. Resolve those from the same cart.
  let ajaxItems = [];
  if (positive.some((line) => line.id && !String(line.id).startsWith('gid://shopify/CartLine/'))) {
    if (payload.cartId) throw eevaPreorderError();
    const cartUrl = (typeof routes !== 'undefined' && routes?.cart_url) || '/cart';
    const response = await eevaWithTimeout(fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' }, signal: options?.signal }));
    if (!response.ok) throw eevaPreorderError();
    const cart = await response.json();
    if (!Array.isArray(cart.items)) throw eevaPreorderError();
    ajaxItems = cart.items;
  }
  const checks = new Map();
  const prepared = [];
  for (const line of positive) {
    let merchandiseId = line.merchandiseId;
    let attributes = [];
    if (line.id) {
      if (String(line.id).startsWith('gid://shopify/CartLine/')) {
        const current = existing.find((item) => item.id.split('?')[0] === String(line.id).split('?')[0]);
        if (!current) throw eevaPreorderError();
        merchandiseId = merchandiseId || current.merchandise?.id;
        attributes = current.attributes || [];
      } else {
        const current = ajaxItems.find((item) => item.key === line.id);
        if (!current) throw eevaPreorderError();
        merchandiseId = merchandiseId || current.variant_id;
        attributes = Object.entries(current.properties || {}).map(([key, value]) => ({ key, value: String(value) }));
      }
    }
    const id = eevaVariantId(merchandiseId);
    if (!checks.has(id)) checks.set(id, eevaPreorderProperties(app, id));
    const properties = await checks.get(id);
    if (properties) {
      const merged = new Map([...attributes, ...(line.attributes || []), ...properties].map(({ key, value }) => [key, value]));
      prepared.push([line, Array.from(merged, ([key, value]) => ({ key, value }))]);
    }
  }
  if (options?.signal?.aborted) throw eevaPreorderError();
  // Prepare the entire request before changing it; a failed check submits nothing.
  for (const [line, attributes] of prepared) line.attributes = attributes;
}


function initStandardActions() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.openCart.configure({
    async handler(defaultHandler) {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
        return;
      }
      return defaultHandler();
    },
  });

  actions.updateCart.configure({
    // Dawn doesn't currently listen for shopify:cart:* events, but the
    // bundle requires an eventTarget. document is the conventional root.
    eventTarget: () => document,
    async handler(defaultHandler, payload, options) {
      try {
        await eevaPrepareAgentCart(payload, options);
      } catch (error) {
        throw eevaPreorderError();
      }
      const result = await defaultHandler();
      try {
        await refreshDawnCartUI();
      } catch (error) {
        console.error('[Dawn] Standard Actions cart refresh failed; reloading.', error);
        window.location.reload();
      }
      return result;
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  initStandardActions();
} else {
  document.addEventListener('DOMContentLoaded', initStandardActions, { once: true });
}

